#!/usr/bin/env node

import { Command } from "commander";
import path from "path";
import fs from "fs-extra";
import ora from "ora";
import chalk from "chalk";
import { fileURLToPath } from "url";
import inquirer from "inquirer";
import { execSync } from "child_process";

export const update = new Command();

const sourceDestPairs = [
  { source: "design-hooks", dest: "design/design-hooks" },
  { source: "assets", dest: "assets" },
  { source: "tailwind.config.ts.txt", dest: "tailwind.config.ts" },
  { source: "components", dest: "design/components" },
  { source: "design-lib", dest: "design/design-lib" },
  { source: "core", dest: "design/core" },
  { source: "design-types", dest: "design/design-types" },
];

const textualExtensions = [".ts", ".tsx", ".js", ".css", ".html", ".json"];

const PACKAGES_TO_UPDATE = [
  "@hookform/resolvers",
  "@radix-ui/react-alert-dialog",
  "@radix-ui/react-aspect-ratio",
  "@radix-ui/react-avatar",
  "@radix-ui/react-checkbox",
  "@radix-ui/react-collapsible",
  "@radix-ui/react-context-menu",
  "@radix-ui/react-dialog",
  "@radix-ui/react-dropdown-menu",
  "@radix-ui/react-hover-card",
  "@radix-ui/react-icons",
  "@radix-ui/react-label",
  "@radix-ui/react-menubar",
  "@radix-ui/react-navigation-menu",
  "@radix-ui/react-popover",
  "@radix-ui/react-progress",
  "@radix-ui/react-radio-group",
  "@radix-ui/react-scroll-area",
  "@radix-ui/react-select",
  "@radix-ui/react-separator",
  "@radix-ui/react-slider",
  "@radix-ui/react-slot",
  "@radix-ui/react-switch",
  "@radix-ui/react-tabs",
  "@radix-ui/react-toast",
  "@radix-ui/react-toggle",
  "@radix-ui/react-tooltip",
  "@tailwindcss/aspect-ratio",
  "framer-motion",
  "highlight.js",
  "lucide-react",
  "next-themes",
  "react-day-picker",
  "react-hook-form",
  "react-markdown",
  "react-textarea-autosize",
  "react-wrap-balancer",
  "styled-components",
  "tailwind-merge",
  "tailwindcss",
  "tailwindcss-animate",
  "usehooks-ts",
  "zod",
  "copy-to-clipboard",
  "@tailwindcss/typography",
  "class-variance-authority",
  "cmdk",
  "remark-gfm",
  "remark-math",
  "react-day-picker",
  "@tanstack/react-table",
];

update
  .name("update")
  .description(
    chalk.bgGreenBright(
      chalk.black(" Update the project by refreshing design system files "),
    ),
  )
  .action(async () => {
    const __filename = fileURLToPath(import.meta.url);
    const __dirname = path.dirname(__filename);
    const SYSTEM_DIR = path.join(__dirname, "../system");

    console.log(
      chalk.cyanBright.bold(
        "🔄 " + chalk.underline("Update Kinesys Design System") + " 🔄",
      ),
    );

    // get the user input for the target directory
    const { targetDir } = await inquirer.prompt([
      {
        type: "input",
        name: "targetDir",
        message: chalk.greenBright.italic(
          "📁 the target directory. defaults to the current directory:",
        ),
        default: "./",
      },
    ]);

    const {
      packageManager,
    }: { packageManager: "pnpm" | "yarn" | "npm" | "bun" } =
      await inquirer.prompt([
        {
          type: "list",
          name: "packageManager",
          message: chalk.greenBright.italic("📦 Choose your package manager:"),
          choices: ["pnpm", "npm", "yarn", "bun"],
          default: "npm",
        },
      ]);

    const spinner = ora({
      text: chalk.hex("#FFA500").bold("Updating... 🛠️"),
      spinner: "star",
      color: "yellow",
    }).start();

    // Update packages
    try {
      spinner.text = chalk.hex("#FFA500").bold("Updating packages... 📦");
      execSync(
        `${packageManager} ${
          packageManager === "yarn" || packageManager === "bun"
            ? "upgrade"
            : "update"
        } ${PACKAGES_TO_UPDATE.join(" ")}`,
        { stdio: "inherit" },
      );
      // uninstall @kinesys/design package
      spinner.text = chalk
        .hex("#FFA500")
        .bold("Updating @kinesys/design... 📦");
      execSync(
        `${packageManager} ${
          packageManager === "yarn" ? "remove" : "uninstall"
        } @kinesys/design`,
        {
          stdio: "ignore",
        },
      );
      // install @kinesys/design package
      execSync(
        `${packageManager} ${
          packageManager === "yarn" ? "add" : "install"
        } @kinesys/design`,
        {
          stdio: "ignore",
        },
      );
    } catch (error) {
      console.error(
        chalk.bgRedBright(
          chalk.black(" An error occurred during package updating: "),
        ) + chalk.red(error),
      );
      return;
    }

    // Update specified files and folders
    const updateFiles = async (source: any, destination: any) => {
      try {
        // Spinner message to indicate which file is currently being processed
        spinner.text = `🗂️  ${chalk.blueBright.bold(
          "Processing:",
        )} ${chalk.blue.underline(source)}`;

        // Check if the source file or directory exists
        const exists = await fs.pathExists(source);
        if (!exists) {
          throw new Error(`Source path "${source}" doesn't exist.`);
        }

        // Retrieve the stats object for the source path
        const stats = await fs.stat(source);

        // If the source is a file, copy it to the destination
        if (stats.isFile()) {
          // Check if the file has a '.txt' extension and another known extension before it
          const ext = path.extname(source);
          if (ext === ".txt") {
            const secondExt = path.extname(path.basename(source, ext));
            if (textualExtensions.includes(secondExt)) {
              // Remove the '.txt' extension from the destination path
              destination = path.join(
                path.dirname(destination),
                path.basename(destination, ".txt"),
              );
            }
          }
          await fs.copy(source, destination);
        }
        // If the source is a directory, ensure the destination directory exists and copy each file
        else if (stats.isDirectory()) {
          await fs.ensureDir(destination);
          const files = await fs.readdir(source);
          for (const file of files) {
            await updateFiles(
              path.join(source, file),
              path.join(destination, file),
            );
          }
        }
      } catch (fileOpError) {
        throw new Error(`Error during file operation: ${fileOpError}`);
      }
    };

    // Error Handling: Wrap initialization logic in try-catch block
    try {
      for (const pair of sourceDestPairs) {
        const srcPath = path.join(SYSTEM_DIR, pair.source);
        const destPath = path.join(targetDir, pair.dest);
        await updateFiles(srcPath, destPath);
      }
      spinner.succeed(chalk.greenBright.bgBlack("🎉 Update complete 🎉"));
    } catch (error) {
      console.error(
        chalk.bgRedBright(
          chalk.black(" An error occurred during initialization: "),
        ) + chalk.red(error),
      );
    }
  });
